// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'
import type { LiteralUnion } from 'type-fest'

import type { QdsFocusEventDetail } from '../../helpers'
import {
  attachInternals,
  determineLinkIcon,
  pickFocusEventAttributes,
} from '../../helpers'
import type { Importance, LinkIcon, Target } from '../shared'

/**
 * `<qds-inline-link>` elements, with their `href` property, create a hyperlink
 * to web pages, files, email addresses, locations in the same page, or
 * anything else a URL can address.
 *
 * Content within each `<qds-inline-link>` *should* indicate the inline link's
 * destination. If the `href` property is present, pressing the enter key while
 * focused on the `<qds-inline-link>` element will activate it.
 *
 * @slot Content to display for the inline link.
 * @see https://quartz.se.com/build/components/inline-link
 */
@Component({
  tag: 'qds-inline-link',
  shadow: { delegatesFocus: true },
  styleUrl: 'inline-link.css',
})
export class InlineLink implements ComponentInterface {
  /**
   * Prevents the inline link from being interacted with: it cannot be pressed
   * or focused.
   */
  @Prop() public readonly disabled: boolean = false

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The inline link's importance.
   */
  @Prop() public readonly importance?: Importance = 'standard'

  /**
   * Causes the browser to treat the linked URL as a download. Can be used with
   * or without a `filename` value:
   *
   * - Without a value, the browser will suggest a filename/extension,
   * generated from various sources:
   *
   *   - The
   *   [`Content-Disposition`](https://developer.mozilla.org/docs/Web/HTTP/Headers/Content-Disposition)
   *   HTTP header
   *   - The final segment in the URL
   *   [path](https://developer.mozilla.org/docs/Web/API/URL/pathname)
   *   - The
   *   [media type](https://developer.mozilla.org/docs/Glossary/MIME_type)
   *   (from the
   *   [`Content-Type`](https://developer.mozilla.org/docs/Web/HTTP/Headers/Content-Type)
   *   header, the start of a
   *   [`data:` URL](https://developer.mozilla.org/docs/Web/HTTP/Basics_of_HTTP/Data_URLs),
   *   or
   *   [`Blob.type`](https://developer.mozilla.org/docs/Web/API/Blob/type)
   *   for a
   *   [`blob:` URL](https://developer.mozilla.org/docs/Web/API/URL/createObjectURL))
   * - `filename`: defining a value suggests it as the filename. `/` and `\`
   * characters are converted to underscores (`_`). Filesystems may forbid
   * other characters in filenames, so browsers will adjust the suggested name
   * if necessary.
   *
   * @webnative
   */
  @Prop() public readonly download?: boolean | string

  /**
   * The URL that the inline link points to. Inline links are not restricted to
   * HTTP-based URLs — they can use any URL scheme supported by browsers:
   *
   * - Sections of a page with document fragments
   * - Specific text portions with
   * [text fragments](https://developer.mozilla.org/docs/Web/Text_fragments)
   * - Pieces of media files with media fragments
   * - Telephone numbers with `tel:` URLs
   * - Email addresses with `mailto:` URLs
   * - While web browsers may not support other URL schemes, websites can with
   * [`registerProtocolHandler()`](https://developer.mozilla.org/docs/Web/API/Navigator/registerProtocolHandler)
   *
   * @webnative
   */
  @Prop() public readonly href?: URL | string

  /**
   * Hints at the human language of the linked URL. No built-in functionality.
   * Allowed values are the same as
   * [the global `lang` attribute](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/lang).
   *
   * @webnative
   */
  @Prop() public readonly hreflang?: string

  /**
   * How much of the
   * [referrer](https://developer.mozilla.org/docs/Web/HTTP/Headers/Referer)
   * to send when following the inline link.
   *
   * - `no-referrer`: The
   * [`Referer`](https://developer.mozilla.org/docs/Web/HTTP/Headers/Referer)
   * header will not be sent.
   * - `no-referrer-when-downgrade`: The
   * [`Referer`](https://developer.mozilla.org/docs/Web/HTTP/Headers/Referer)
   * header will not be sent to
   * [origin](https://developer.mozilla.org/docs/Glossary/Origin)s
   * without [TLS](https://developer.mozilla.org/docs/Glossary/TLS)
   * ([HTTPS](https://developer.mozilla.org/docs/Glossary/HTTPS)).
   * - `origin`: The sent referrer will be limited to the origin of the
   * referring page: its
   * [scheme](https://developer.mozilla.org/docs/Learn/Common_questions/What_is_a_URL),
   * [host](https://developer.mozilla.org/docs/Glossary/Host), and
   * [port](https://developer.mozilla.org/docs/Glossary/Port).
   * - `origin-when-cross-origin`: The referrer sent to other origins will be
   * limited to the scheme, the host, and the port. Navigations on the same
   * origin will still include the path.
   * - `same-origin`: A referrer will be sent for
   * [same origin](https://developer.mozilla.org/docs/Glossary/Same-origin_policy),
   * but cross-origin requests will contain no referrer information.
   * - `strict-origin`: Only send the origin of the document as the referrer
   * when the protocol security level stays the same (HTTPS→HTTPS), but don't
   * send it to a less secure destination (HTTPS→HTTP).
   * - `strict-origin-when-cross-origin` (default): Send a full URL when
   * performing a same-origin request, only send the origin when the protocol
   * security level stays the same (HTTPS→HTTPS), and send no header to a less
   * secure destination (HTTPS→HTTP).
   * - `unsafe-url`: The referrer will include the origin and the path (but not the
   * [fragment](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/hash),
   * [password](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/password),
   * or [username](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/username)).
   * **This value is unsafe** because it leaks origins and paths from
   * TLS-protected resources to insecure origins.
   *
   * @webnative
   */
  @Prop({ attribute: 'referrerpolicy' })
  public readonly referrerPolicy?: ReferrerPolicy

  /**
   * The relationship of the linked URL as space-separated
   * [link types](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel).
   *
   * The following list includes some of the most important existing keywords.
   * Every keyword within a space-separated value should be unique within that
   * value.
   *
   * - [`alternate`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-alternate):
   * Alternate representations of the current document.
   * - [`author`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-author):
   * Author of the current document or article.
   * - [`bookmark`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-bookmark):
   * Permalink for the nearest ancestor section.
   * - [`external`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-external):
   * The referenced document is not part of the same site as the current
   * document. An icon will be rendered next to the text if this value is
   * specified.
   * - [`help`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-help):
   * Link to context-sensitive help.
   * - [`license`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-license):
   * Indicates that the main content of the current document is covered by the
   * copyright license described by the referenced document.
   * - [`me`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel/me):
   * Indicates that the current document represents the person who owns the
   * linked content.
   * - [`next`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-next):
   * Indicates that the current document is a part of a series and that the
   * next document in the series is the referenced document.
   * - [`nofollow`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-nofollow):
   * Indicates that the current document's original author or publisher does
   * not endorse the referenced document.
   * - [`noopener`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel/noopener):
   * Creates a top-level browsing context that is not an auxiliary browsing
   * context if the hyperlink would create either of those, to begin with (i.e.,
   * has an appropriate `target` attribute value).
   * - [`noreferrer`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-noreferrer):
   * No `Referer` header will be included. Additionally, has the same effect as
   * `noopener`.
   * - [`opener`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-opener):
   * Creates an auxiliary browsing context if the hyperlink would otherwise
   * create a top-level browsing context that is not an auxiliary browsing
   * context (i.e., has "`_blank`" as `target` attribute value).
   * - [`prev`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-prev):
   * Indicates that the current document is a part of a series and that the
   * previous document in the series is the referenced document.
   * - [`search`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-search):
   * Gives a link to a resource that can be used to search through the current
   * document and its related pages.
   * - [`tag`](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel#attr-tag):
   * Gives a tag (identified by the given address) that applies to the current
   * document.
   *
   * @webnative
   */
  @Prop() public readonly rel?: string

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<Target, string>

  /**
   * Emitted when the inline link loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the inline link gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLElement

  @State() private linkIcon?: LinkIcon

  @State() private tabIndex?: number

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  readonly #internals: ElementInternals = attachInternals.call(this)

  get #computedImportance(): Importance {
    switch (this.importance) {
      case 'subdued':
      case 'emphasized': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.disabled) event.stopImmediatePropagation()
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    this.#internals.ariaDisabled = this.disabled.toString()
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    // FIXME: `undefined` instead of `0` causes `qdsFocus/qdsBlur` E2E tests
    // to fail, but it's not clear why this is the case since it works for
    // other components.
    this.tabIndex = Number.isNaN(parsedValue) ? 0 : parsedValue
  }

  @Watch('href')
  @Watch('target')
  protected hrefTargetChanged(): void {
    this.linkIcon = determineLinkIcon(this.href, this.target)
  }

  public componentWillLoad(): void {
    this.#internals.role = 'link'

    this.disabledChanged()
    this.hrefTargetChanged()
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public componentDidLoad(): void {
    this.#internals.ariaLabel = this.host.textContent
  }

  public render() {
    return (
      <a
        aria-hidden="true"
        class={{
          'qds-inline-link': true,
          'qds-disabled': this.disabled,
        }}
        data-importance={this.#computedImportance}
        download={this.download}
        href={this.href?.toString()}
        hrefLang={this.hreflang}
        onBlur={this.#onBlur}
        onFocus={this.#onFocus}
        referrerPolicy={this.referrerPolicy}
        rel={this.rel}
        tabIndex={this.disabled ? -1 : this.tabIndex}
        target={this.target}
      >
        {this.iconName !== undefined && this.iconName !== '' && (
          <qds-icon
            class="qds-icon"
            library={this.iconLibrary}
            name={this.iconName}
          />
        )}
        <slot onSlotchange={this.#onSlotchange} />
        {this.linkIcon && (
          <qds-icon class="qds-icon" library="core" name={this.linkIcon} />
        )}
      </a>
    )
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onSlotchange = (): void => {
    this.#internals.ariaLabel = this.host.textContent
  }
}
